import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ShieldCheckIcon, ChevronLeftIcon, ChevronRightIcon, SparklesIcon, CheckIcon } from 'lucide-react';
import { ListingDetailModal } from './ListingDetailModal';
import { useAuth } from './AuthContext';
export interface ListingCardProps {
  id: string;
  title: string;
  price: string;
  location: string;
  images: string[];
  specs: {
    label: string;
    value: string;
  }[];
  matchScore: number;
  sellerType: 'Dealer' | 'Individual' | 'Verified Business';
  isSaved?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}
export function ListingCard(props: ListingCardProps) {
  const {
    title,
    price,
    location,
    images,
    specs,
    matchScore,
    sellerType,
    isSaved = false
  } = props;
  const {
    selected = false,
    onToggleSelect
  } = props;
  const {
    requireAuth
  } = useAuth();
  const [currentImage, setCurrentImage] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImage(prev => (prev + 1) % images.length);
  };
  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImage(prev => (prev - 1 + images.length) % images.length);
  };
  const sellerBadge = sellerType === 'Verified Business' ? 'bg-success text-white' : sellerType === 'Dealer' ? 'bg-dark-navy text-white' : 'bg-white/90 text-slate-700';
  return <motion.div whileHover={{
    y: -4
  }} className={`transform-gpu will-change-transform bg-white rounded-2xl border overflow-hidden shadow-soft hover:shadow-medium transition-all group flex flex-col h-full cursor-pointer ${selected ? 'border-primary-indigo ring-2 ring-primary-indigo/30' : 'border-slate-200'}`} data-id="element-1720">
      {/* Image Carousel */}
      <div className="relative aspect-[16/11] overflow-hidden bg-slate-100" data-id="element-1721">
        <img src={images[currentImage]} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1722" />

        {/* AI Match Badge */}
        <div className="absolute top-2.5 left-2.5 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm border border-white/20" data-id="element-1723">
          <SparklesIcon className="w-3.5 h-3.5 text-ai-purple" data-id="element-1724" />
          <span className="text-xs font-bold bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-1725">
            {matchScore}% Match
          </span>
        </div>

        {/* Seller Type Tag */}
        <div className={`absolute top-2.5 right-2.5 px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm backdrop-blur-sm text-[11px] font-semibold ${sellerBadge}`} data-id="element-1726">
          {sellerType === 'Verified Business' && <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-1727" />}
          {sellerType}
        </div>

        {/* Carousel Controls */}
        {images.length > 1 && <>
            <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white" data-id="element-1728">
              <ChevronLeftIcon className="w-5 h-5 text-slate-700" data-id="element-1729" />
            </button>
            <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white" data-id="element-1730">
              <ChevronRightIcon className="w-5 h-5 text-slate-700" data-id="element-1731" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-1732">
              {images.map((_, idx) => <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImage ? 'bg-white w-3' : 'bg-white/50'}`} data-id="element-1733" />)}
            </div>
          </>}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1" data-id="element-1734">
        <h3 className="font-display font-bold text-[15px] text-dark-navy line-clamp-1 mb-0.5" data-id="element-1735">
          {title}
        </h3>
        <div className="font-display font-extrabold text-lg text-primary-indigo mb-2" data-id="element-1736">
          {price}
        </div>

        <div className="flex items-center gap-1.5 text-slate-500 text-[13px] mb-3" data-id="element-1737">
          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1738" />
          <span className="truncate" data-id="element-1739">{location}</span>
        </div>

        {/* Specs Grid */}
        <div className="grid grid-cols-2 gap-1.5 mb-3" data-id="element-1740">
          {specs.map((spec, idx) => <div key={idx} className="bg-slate-50 rounded-lg px-2.5 py-1.5" data-id="element-1741">
              <span className="text-slate-500 block text-[11px] mb-0.5" data-id="element-1742">
                {spec.label}
              </span>
              <span className="font-medium text-slate-700 text-[13px] truncate block" data-id="element-1743">
                {spec.value}
              </span>
            </div>)}
        </div>

        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center gap-2" data-id="element-1744">
          {onToggleSelect && <button onClick={e => {
          e.stopPropagation();
          onToggleSelect();
        }} title={selected ? 'Selected — ask AI about this' : 'Select to ask AI'} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg transition-all shadow-sm hover:shadow-md whitespace-nowrap ${selected ? 'bg-dark-navy text-white' : 'bg-gradient-to-r from-primary-indigo to-ai-purple text-white hover:brightness-110'}`} data-id="element-1745">
              {selected ? <CheckIcon className="w-4 h-4" data-id="element-1746" /> : <SparklesIcon className="w-4 h-4" data-id="element-1747" />}
              {selected ? 'Selected' : 'Ask AI'}
            </button>}
          <button onClick={e => {
          e.stopPropagation();
          // Browsing is free; full details require registration
          requireAuth(() => setShowDetails(true));
        }} className="flex-1 flex items-center justify-center px-4 py-1.5 border border-slate-200 text-slate-700 text-[13px] font-medium rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors" data-id="element-1748">
            Details
          </button>
        </div>
      </div>

      <ListingDetailModal listing={props} open={showDetails} onClose={() => setShowDetails(false)} data-id="element-1749" />
    </motion.div>;
}